import { useState, useMemo, useContext, memo } from "react";

import { Select, SelectItem, Card, Text, Switch, Button } from "@aidron/aidron-ds";

import { TenantContext } from "../../../templates/context/TenantContext";
import { i18n } from "../../../translate/i18n";

import {
  ButtonWithModal,
  FilterButton,
  HierarchyFilters,
  TableDownloadButton 
} from "../sharedComponents.index";

import { ButtonDisplayStrategy } from "../../../viewModel/strategy/buttons/buttons.strategy";

import { CustomHeading } from "../../StyledComponents/styleComponents.index";

import PropTypes from 'prop-types';

const { t } = i18n;

const ActionsSection = memo(function ActionsSection({ actions, uiState }) {

  const { checkedProducts, renderGraphicAnalysis } = actions || {};
  const { downloadButton } = uiState || {};

  const buttonCtx = useMemo(() => ({
    label: t("analyzeProduct"),
    type: "filled",
    icon: "",
    size: "lg",
    renderModalContent: renderGraphicAnalysis
  }), [renderGraphicAnalysis]);

  const buttonUiState = useMemo(() => ({
    disabled: !checkedProducts.length
  }), [checkedProducts.length]);

  return (
    <>
      {downloadButton && 
        <TableDownloadButton  
          ctx={{
            fileName: "forecast",
            sheetName: "Forecast",
            defaultFileName: "forecast"
          }}
          uiState={{
            size: "lg"
          }}/>
      }

      <div style={{ display: 'flex', alignItems: 'center' }}>
        {renderGraphicAnalysis && (
          <ButtonWithModal
            ctx={buttonCtx}
            uiState={buttonUiState}
          />
        )}
      </div>
    </>
  );
});

const FilterSection = ({ actions, ctx, uiState }) => {

  const [showFilters, setShowFilters] = useState(false);
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);  

  const {
    checkedProducts,
    renderGraphicAnalysis,
    setSelectedProductIds
  } = actions || {};

  const {
    productIds,
    selectedProductIds,
    products
  } = ctx || {};  

  const { downloadButton } = uiState || {};

  const actionsSectionActions = useMemo(() => ({
    checkedProducts,
    renderGraphicAnalysis,
  }), [checkedProducts, renderGraphicAnalysis]);

  const actionsSectionUiState = useMemo(() => ({
    downloadButton,
  }), [downloadButton]);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', inlineSize: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', inlineSize: 'fit-content' }}>
        <FilterButton 
          actions={{ setShowFilters: setShowFilters }}
          uiState={{ showFilters: showFilters, appliedFiltersCount: appliedFiltersCount }}/>
        
        <ActionsSection 
          actions={actionsSectionActions}
          uiState={actionsSectionUiState}
        />
      </div>

      <HierarchyFilters
        actions={{ setAppliedFiltersCount: setAppliedFiltersCount, setSelectedProductIds: setSelectedProductIds }}
        ctx={{ productIds: productIds, selectedProductIds: selectedProductIds, allProducts: products }}
        uiState={{ showFilters: showFilters }}/> 
    </section>
  )
};

export function NewTableOptions({ actions, ctx, uiState }) {

  const { hierarchy } = useContext(TenantContext);

  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const {
    checkedProducts = [],
    renderGraphicAnalysis = () => {},
    setFreezeColumns = () => {},
    setSelectedHierarchyIndexes = () => {},
    setSelectedProductIds = () => {},
    setShowCancelModal = () => {},
    setShowInputs = () => {},
    onInputsSave = () => {},
    setShowFormModal = () => {}
  } = actions || {};

  const { selectedHierarchyIndexes, productIds, selectedProductIds, products } = ctx || {};  

  const { 
    freezeColumns = false,
    downloadButton,
    hierarchyColumnsButton,
    showFilterButton = true,
    showCustomHeading = false,
    labels,
    showInputs,
    actionButtons = false,
  } = uiState || {};

  const handleChange = (options) => {
    if (options.length === 0) return;
    setSelectedHierarchyIndexes(options);
  };

  return (
    <div style={{ marginBottom: '10px', inlineSize: '100%' }}>
      <Card>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'start',
            justifyContent: 'space-between',
            inlineSize: '100%'
          }}>
          {showCustomHeading && (
            <CustomHeading fontSize={['0.5rem', '0.9rem', '1.2rem']} margin="0">
              {labels.titlePage ?? ""}
            </CustomHeading>     
          )}

          {showFilterButton && (
            <FilterSection 
              actions={{
                checkedProducts,
                renderGraphicAnalysis,
                setSelectedProductIds
              }}
              ctx={{
                productIds,
                selectedProductIds,
                products: products     
              }}
              uiState={{
                downloadButton,
                showFilterButton
              }}/>              
          )}

          {hierarchyColumnsButton && (
            <Button
              onClick={() => setShowColumnFilters(!showColumnFilters)}
              label=""
              icon="layout"
              type={showColumnFilters ? "filled" : "outlined"}
              size="lg"/>
          )} 

          {actionButtons && (
            <ButtonDisplayStrategy 
              mode={"prefixes"}
              actions={{
                setShowInputs: setShowInputs,
                setShowCancelModal: setShowCancelModal,
                onInputsSave: onInputsSave,
                setShowFormModal: setShowFormModal
              }}
              uiState={{
                customDisabledCondition: true,
                showInputs: showInputs,
              }}
            />
          )}

        </div>
      </Card>

      {showColumnFilters && (
        <Card
          style={{
            margin: "10px 0",
            padding: "10px 20px 10px 30px",
            backgroundColor: "rgb(252, 252, 252)",
          }}
        >
          <div className="margin-on-switch-outer">
            <Switch handleChange={option => setFreezeColumns(option)} value={freezeColumns}>
              {t("freezeColumns")}
            </Switch>
          </div>

          <Text size="sm" weight="bold">
            {t("choseColumns")}
          </Text>

          <div className="mb-2 mx-2">
            <Select readOnly multiselect label="" handleChange={handleChange} selected={selectedHierarchyIndexes}>
              {hierarchy.levels.map((option, index) => (
                <SelectItem key={index} value={index}>
                  {hierarchy.labels[option]}
                </SelectItem>
              ))}
            </Select>
          </div>
        </Card>
      )}       

    </div>
  );
};

NewTableOptions.propTypes = {
  actions: PropTypes.object,
  ctx: PropTypes.object,
  uiState: PropTypes.object
}